import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { message } from "antd";
import {
  fetchUserPreferences,
  saveFavoriteWorkflows,
  saveSwitcherVisibility,
  saveUserPreferences,
} from "../api/userPreferencesApi";

// Retry an async read with exponential backoff. A single transient failure of
// this read decides the onboarding gate: the persona slice reads it, and a
// rejection makes the gate treat the user as un-onboarded and run the mandatory
// wizard. Retrying absorbs a blip so only a persistent failure reaches that
// branch. Delays are 0.5s, 1s, 2s across the three attempts.
const RETRY_DELAYS_MS = [500, 1000, 2000];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const fetchWithRetry = async (attempt) => {
  for (let i = 0; ; i += 1) {
    try {
      return await attempt();
    } catch (err) {
      if (i >= RETRY_DELAYS_MS.length) throw err;
      await sleep(RETRY_DELAYS_MS[i]);
    }
  }
};

// Async thunk: fetch all preferences from backend (all workspaces at once).
//
// Resolves the uid from state rather than an argument, so every call site
// (App root plus three page-level hooks) carries it without knowing to. The
// persona slice needs it: the API token identifies the caller, so under
// impersonation this response is the ADMIN's, and stamping it with the uid it
// was fetched FOR is what stops it being read as the target's.
export const fetchUserPreferencesFromBackend = createAsyncThunk(
  "userPreferences/fetchFromBackend",
  async (_, { getState, rejectWithValue }) => {
    const uid = getState()?.auth?.user?.uid ?? null;
    try {
      const data = await fetchWithRetry(fetchUserPreferences);
      return { data, uid };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// "View as User" is a read-only lens: the API refuses every write under an
// impersonation session with a 403. Cancelling here, before dispatch, is what
// keeps the client from sending a request it knows will be refused, and it
// covers every call site of these thunks rather than each hook separately.
// A cancelled thunk dispatches no `rejected` action, so no reducer or toast
// reacts to it. Other slices' own-account writes share this condition.
export const notImpersonating = (_, { getState }) =>
  !getState().auth?.isImpersonating;

// Async thunk: save preferences for a specific workspace+table
export const saveUserPreferencesToBackend = createAsyncThunk(
  "userPreferences/saveToBackend",
  async ({ id_client, table_id, preferences }, { rejectWithValue }) => {
    try {
      await saveUserPreferences({ id_client, table_id, preferences });
      return { id_client, table_id };
    } catch (err) {
      // Surface failure so users don't silently lose column preferences.
      // The 1.5s-debounced caller fires at most once per resize / column
      // toggle, so this won't spam.
      message.error("Failed to save column preferences");
      return rejectWithValue(err.message);
    }
  },
  { condition: notImpersonating }
);

// Async thunk: persist the user's full favorite-workflow list. The frontend
// owns the list and sends the whole array on every toggle (mirroring how
// column prefs are PUT wholesale). The list is per-user, not per-workspace.
export const saveFavoriteWorkflowsToBackend = createAsyncThunk(
  "userPreferences/saveFavoriteWorkflows",
  async (workflowIds, { rejectWithValue }) => {
    try {
      await saveFavoriteWorkflows(workflowIds);
      return workflowIds;
    } catch (err) {
      // Best-effort sync: the toggle is applied optimistically, so it holds for
      // the life of the tab and a failed save costs the user only their next
      // reload. Firestore owns the list, so nothing keeps a local copy that
      // could disagree with it. Unlike column preferences, favorites are
      // low-stakes and toggled one-at-a-time, so a red error toast on every
      // star click is noise — log it for diagnostics instead (see the
      // `.rejected` reducer).
      return rejectWithValue(err.message);
    }
  },
  { condition: notImpersonating }
);

// Async thunk: persist the user's switcher visibility (focus mode). UI-only —
// never affects access. Best-effort like favorites: the change is applied
// optimistically, so it holds until the tab reloads.
export const saveSwitcherVisibilityToBackend = createAsyncThunk(
  "userPreferences/saveSwitcherVisibility",
  /** @param {{ switcher_focus_mode: boolean }} arg */
  async ({ switcher_focus_mode }, { rejectWithValue }) => {
    try {
      await saveSwitcherVisibility({ switcher_focus_mode });
      return { switcher_focus_mode };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
  { condition: notImpersonating }
);

// State shape is `state.userPreferences.column_preferences[id_client][tableId]`.
// The outer slice name follows JS Redux convention (camelCase); the inner
// field stays snake_case to mirror the Firestore field name 1:1, so the
// thunk's `fulfilled` reducer can spread the backend payload without
// remapping keys. Selectors that cross this boundary should reach in via
// `state.userPreferences?.column_preferences?.[id_client]?.[tableId]`.
const initialState = {
  // { [id_client]: { homepage: { columnWidths, visibleBasicColumns, ... }, physicalLocation: {...}, physicalFranchise: {...} } }
  column_preferences: {},
  // Single per-user list of favorited workflow IDs (no workspace dimension).
  // Workspace-scoped visibility filtering happens at render time against the
  // active workspace's visible workflow IDs.
  favorite_workflows: [],
  // Workspace-switcher focus mode ("Presenting"), staff-facing. UI-only — it
  // limits the switcher to the current workspace and never affects access.
  // Mirrors the Firestore field name 1:1 so the fetch payload needs no remap.
  switcher_focus_mode: false,
  backendSyncStatus: "idle", // "idle" | "loading" | "synced" | "error"
};

const userPreferencesSlice = createSlice({
  name: "userPreferences",
  initialState,
  reducers: {
    setColumnWidth: (state, action) => {
      const { workspaceId, tableId, columnKey, width } = action.payload;
      if (!workspaceId) return;
      if (!state.column_preferences[workspaceId]) state.column_preferences[workspaceId] = {};
      if (!state.column_preferences[workspaceId][tableId]) state.column_preferences[workspaceId][tableId] = {};
      if (!state.column_preferences[workspaceId][tableId].columnWidths) {
        state.column_preferences[workspaceId][tableId].columnWidths = {};
      }
      state.column_preferences[workspaceId][tableId].columnWidths[columnKey] = width;
    },
    setVisibleColumns: (state, action) => {
      const { workspaceId, tableId, columnGroup, columns } = action.payload;
      if (!workspaceId) return;
      if (!state.column_preferences[workspaceId]) state.column_preferences[workspaceId] = {};
      if (!state.column_preferences[workspaceId][tableId]) state.column_preferences[workspaceId][tableId] = {};
      state.column_preferences[workspaceId][tableId][columnGroup] = columns;
    },
    // Toggle a workflow in/out of the per-user favorites list. Optimistic:
    // the caller dispatches saveFavoriteWorkflowsToBackend with the resulting
    // list to persist. Newly favorited IDs append to the end (preserves the
    // order favorites were added in).
    toggleFavoriteWorkflow: (state, action) => {
      const workflowId = action.payload;
      if (!workflowId) return;
      if (!Array.isArray(state.favorite_workflows)) {
        state.favorite_workflows = [];
      }
      const idx = state.favorite_workflows.indexOf(workflowId);
      if (idx === -1) {
        state.favorite_workflows.push(workflowId);
      } else {
        state.favorite_workflows.splice(idx, 1);
      }
    },
    // Optimistically set switcher focus mode (show only the current
    // workspace); the caller dispatches saveSwitcherVisibilityToBackend to
    // persist.
    setSwitcherFocusMode: (state, action) => {
      state.switcher_focus_mode = Boolean(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserPreferencesFromBackend.pending, (state) => {
        state.backendSyncStatus = "loading";
      })
      .addCase(fetchUserPreferencesFromBackend.fulfilled, (state, action) => {
        const data = action.payload?.data;
        const backendColPrefs = data?.column_preferences;
        if (backendColPrefs && typeof backendColPrefs === "object") {
          // Backend wins: merge per-table across all workspaces
          Object.entries(backendColPrefs).forEach(([wsId, wsTables]) => {
            if (!state.column_preferences[wsId]) {
              state.column_preferences[wsId] = {};
            }
            Object.entries(wsTables).forEach(([tableId, tablePrefs]) => {
              state.column_preferences[wsId][tableId] = {
                ...state.column_preferences[wsId][tableId],
                ...tablePrefs,
              };
            });
          });
        }
        // Backend is source of truth for the favorites list too.
        if (Array.isArray(data?.favorite_workflows)) {
          state.favorite_workflows = data.favorite_workflows;
        }
        // Backend is source of truth for switcher focus mode too.
        if (typeof data?.switcher_focus_mode === "boolean") {
          state.switcher_focus_mode = data.switcher_focus_mode;
        }
        state.backendSyncStatus = "synced";
      })
      .addCase(fetchUserPreferencesFromBackend.rejected, (state) => {
        // The read failed, so the slice keeps whatever this tab already has:
        // initial state on a cold load, the user's own edits after that.
        state.backendSyncStatus = "error";
      })
      .addCase(saveUserPreferencesToBackend.rejected, (_, action) => {
        console.error("[userPreferences] Failed to save to backend:", action.payload);
      })
      .addCase(saveFavoriteWorkflowsToBackend.rejected, (_, action) => {
        console.error(
          "[userPreferences] Failed to save favorite workflows:",
          action.payload
        );
      })
      .addCase(saveSwitcherVisibilityToBackend.rejected, (_, action) => {
        console.error(
          "[userPreferences] Failed to save switcher visibility:",
          action.payload
        );
      });
  },
});

export const {
  setColumnWidth,
  setVisibleColumns,
  toggleFavoriteWorkflow,
  setSwitcherFocusMode,
} = userPreferencesSlice.actions;
export default userPreferencesSlice.reducer;
